import { main_url } from "../pages/basefile";
import { getValidToken } from "../auth/getToken";

async function getAuthHeaders() {
  const token = (await getValidToken()) || localStorage.getItem("authToken");
  if (!token) {
    throw new Error("Authentication required. Redirecting to login.");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchPatientDatagridRows({
  page = 0,
  pageSize = 10,
  search = "",
  clientId = "",
  projectId = "",
  patientStatus = "",
  enrollmentDateFrom = "",
  enrollmentDateTo = "",
  sortField = "created_at",
  sortOrder = "desc",
}) {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sortField,
    sortOrder,
  });
  if (search) params.set("search", search);
  if (clientId) params.set("clientId", clientId);
  if (projectId) params.set("projectId", String(projectId));
  if (patientStatus) params.set("patientStatus", patientStatus);
  if (enrollmentDateFrom) params.set("enrollmentDateFrom", enrollmentDateFrom);
  if (enrollmentDateTo) params.set("enrollmentDateTo", enrollmentDateTo);

  const response = await fetch(`${main_url}/api/v1/patients/datagrid/rows?${params.toString()}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch subjects");
  }
  return response.json();
}

export async function generatePatientPid({ client_id, project_id }) {
  const params = new URLSearchParams({
    client_id,
    project_id: String(project_id),
  });
  const response = await fetch(`${main_url}/api/v1/patients/generate-pid?${params.toString()}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to generate PID");
  }
  return response.json();
}

export async function createPatient(payload) {
  const response = await fetch(`${main_url}/api/v1/patients`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to create subject");
  }
  return response.json();
}

export async function updatePatient(patientId, payload) {
  const response = await fetch(`${main_url}/api/v1/patients/${patientId}`, {
    method: "PUT",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to update subject");
  }
  return response.json();
}

export async function getPatientById(patientId) {
  const response = await fetch(`${main_url}/api/v1/patients/${patientId}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch subject");
  }
  return response.json();
}

export async function deletePatient(patientId) {
  const response = await fetch(`${main_url}/api/v1/patients/${patientId}`, {
    method: "DELETE",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to delete subject");
  }
}

export async function fetchPatientDocuments(patientId) {
  const response = await fetch(`${main_url}/api/v1/patients/${patientId}/documents`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch subject documents");
  }
  return response.json();
}

export async function fetchPatientSchedules(patientId) {
  const response = await fetch(`${main_url}/api/v1/patients/${patientId}/schedules`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch subject schedules");
  }
  return response.json();
}

export async function fetchPatientScheduleQuestionnaires(patientId, executionId, scheduleId) {
  const response = await fetch(
    `${main_url}/api/v1/patients/${patientId}/executions/${executionId}/schedules/${scheduleId}/questionnaires`,
    {
      method: "GET",
      headers: await getAuthHeaders(),
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch schedule questionnaires");
  }
  return response.json();
}

export async function submitPatientScheduleQuestionnaireOnBehalf({
  patientId,
  executionId,
  scheduleId,
  qid,
  answersJson,
}) {
  const response = await fetch(
    `${main_url}/api/v1/patients/${patientId}/executions/${executionId}/schedules/${scheduleId}/questionnaires/${qid}`,
    {
      method: "POST",
      headers: await getAuthHeaders(),
      body: JSON.stringify(answersJson || {}),
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to submit questionnaire on behalf of subject");
  }
  return response.json();
}

export async function fetchPatientSubmissions(patientId) {
  const response = await fetch(`${main_url}/api/v1/patients/${patientId}/submissions`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch subject submissions");
  }
  return response.json();
}

export function getPatientSubmissionAnswersPdfUrl(patientId, submissionId) {
  return `${main_url}/api/v1/patients/${patientId}/submissions/${submissionId}/answers-pdf`;
}

export async function updatePatientScheduleDate(patientId, scheduleId, actual_date) {
  const response = await fetch(`${main_url}/api/v1/patients/${patientId}/schedules/${scheduleId}`, {
    method: "PATCH",
    headers: await getAuthHeaders(),
    body: JSON.stringify({ actual_date }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to update subject schedule");
  }
  return response.json();
}

export async function resyncPatientSchedules(patientId) {
  const response = await fetch(`${main_url}/api/v1/patients/${patientId}/schedules/resync`, {
    method: "POST",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to resync subject schedules");
  }
  return response.json();
}

export async function uploadPatientDocument(patientId, { doctype, document_name, file }) {
  const token = (await getValidToken()) || localStorage.getItem("authToken");
  if (!token) {
    throw new Error("Authentication required. Redirecting to login.");
  }
  const formData = new FormData();
  formData.append("doctype", doctype);
  formData.append("document_name", document_name);
  formData.append("file", file);

  const response = await fetch(`${main_url}/api/v1/patients/${patientId}/documents`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to upload subject document");
  }
  return response.json();
}

export function getPatientDocumentDownloadUrl(patientId, documentId) {
  return `${main_url}/api/v1/patients/${patientId}/documents/${documentId}/download`;
}

export async function deletePatientDocument(patientId, documentId) {
  const response = await fetch(`${main_url}/api/v1/patients/${patientId}/documents/${documentId}`, {
    method: "DELETE",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to delete subject document");
  }
}
